import { BybSlider, BybSwitch } from "@Components/common/byb-ds";
import Icon from "@Components/generic/icon";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import useI18n from "@i18n";
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography, useTheme } from "@mui/material";
import { ScenarioConfig } from "@Shared/schemas/scenarios/scenarioSchemas";
import React, { useCallback } from "react";

interface ScenarioSettingsProps {
    config: Partial<ScenarioConfig>;
    onChange: (config: Partial<ScenarioConfig>) => void;
    title?: string;
}

const SliderWithValue = React.memo(({ 
    label, 
    value, 
    onChange, 
    min, 
    max, 
    step, 
    postfix 
}: { 
    label: string;
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    step?: number;
    postfix?: string;
}) => {
    const theme = useTheme();
    const { i18n } = useI18n();
    
    return (
        <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: 1 }}>
                <BybSlider
                    label={label}
                    value={value}
                    min={min}
                    max={max}
                    step={step}
                    onChange={onChange}
                />
            </Box>
            <Typography variant="textBase500" color="dsNeutral.v400" sx={{ minWidth: theme.spacing(8), textAlign: 'right' }}>
                {value}{postfix}
            </Typography>
        </Stack>
    );
});

const SwitchControl = React.memo(({ 
    label, 
    checked, 
    onChange 
}: { 
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
}) => (
    <BybSwitch
        label={label}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
    />
));

const SettingsContent = React.memo(({ config, onChange }: { config: Partial<ScenarioConfig>; onChange: (key: keyof ScenarioConfig, value: any) => void; }) => {
    const handleChange = useCallback((key: keyof ScenarioConfig, value: any) => {
        onChange(key, value);
    }, [onChange]);

    const { i18n } = useI18n();

    return (
        <Stack spacing={3}>
            <SliderWithValue
                label={i18n("playlists.playlist.scenario.settings.game-speed")}
                value={config.game_speed ?? 100}
                min={10}
                max={150}
                step={5}
                postfix="%"
                onChange={(value) => handleChange("game_speed", value)}
            />

            <Stack direction="row" alignItems="center" spacing={2}>
                <SwitchControl
                    label={i18n("playlists.playlist.scenario.settings.bullet-time")}
                    checked={config.bullet_time_decisions ?? false}
                    onChange={(checked) => handleChange("bullet_time_decisions", checked)}
                />

                <SwitchControl
                    label={i18n("playlists.playlist.scenario.settings.mirrored")}
                    checked={config.mirrored ?? false}
                    onChange={(checked) => handleChange("mirrored", checked)}
                />
            </Stack>

            <Typography variant="textLg700" color="dsBase.v200">
                {i18n("playlists.playlist.scenario.settings.awareness.title")}
            </Typography>

            <SliderWithValue
                label={i18n("playlists.playlist.scenario.settings.awareness.player-count")}
                value={config.memory_player_count ?? 5}
                min={1}
                max={21}
                onChange={(value) => handleChange("memory_player_count", value)}
            />

            <SliderWithValue
                label={i18n("playlists.playlist.scenario.settings.awareness.time-limit")}
                value={config.memory_time_limit ?? 10}
                min={4}
                max={45}
                onChange={(value) => handleChange("memory_time_limit", value)}
            />

            <SliderWithValue
                label={i18n("playlists.playlist.scenario.settings.awareness.distance")}
                value={config.memory_distance ?? 30}
                min={10}
                max={100}
                onChange={(value) => handleChange("memory_distance", value)}
            />
        </Stack>
    );
});

const ScenarioSettings = ({ config, onChange, title }: ScenarioSettingsProps) => {
    const theme = useTheme();
    const { i18n } = useI18n();

    const handleChange = useCallback((key: keyof ScenarioConfig, value: any) => {
        onChange({ ...config, [key]: value });
    }, [config, onChange]);

    // If there's a title, it's a scenario-specific settings dialog
    if (title) {
        return (
            <Stack spacing={3}>
                <Typography variant="textLg700" color="dsBase.v200">
                    {title}
                </Typography>
                <SettingsContent config={config} onChange={handleChange} />
            </Stack>
        );
    }

    // Otherwise, it's the global game settings with accordion
    return (
        <Accordion 
            defaultExpanded={false}
            sx={{
                backgroundColor: 'transparent',
                '&:before': {
                    display: 'none',
                },
            }}
        >
            <AccordionSummary 
                expandIcon={<Icon icon={faChevronDown} color={theme.palette.dsNeutral.v400} />}
                sx={{
                    padding: 0,
                    minHeight: 'unset',
                    '& .MuiAccordionSummary-content': {
                        margin: 0,
                    },
                }}
            >
                <Typography variant="textLg700" color="dsBase.v200">
                    {i18n("playlists.playlist.scenario.settings.title")}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: theme.spacing(2, 0) }}>
                <SettingsContent config={config} onChange={handleChange} />
            </AccordionDetails>
        </Accordion>
    );
};

export default ScenarioSettings;